'use client';

import { Button } from '@cotera/client/v0/components/ui/button';
import { cn } from '@cotera/client/v0/lib/utils';
import type { Element } from 'hast';
import { CheckIcon, CopyIcon } from 'lucide-react';
import {
  type ComponentProps,
  createContext,
  type HTMLAttributes,
  useContext,
  useEffect,
  useState,
} from 'react';
import { type BundledLanguage, codeToHtml, type ShikiTransformer } from 'shiki';

type CodeBlockProps = HTMLAttributes<HTMLDivElement> & {
  code: string;
  language: BundledLanguage;
  showLineNumbers?: boolean;
  /**
   * Wrap long lines instead of scrolling horizontally, giving each wrapped line
   * a hanging indent that matches its own leading whitespace (so wrapped JSON
   * continuations stay aligned with the line's content).
   */
  wrapLines?: boolean;
};

// Classes that make shiki's inline `.line` spans wrap. The per-line hanging
// indent itself is applied as an inline style by `hangingIndentTransformer`,
// since it varies per line and can't be expressed in a static class.
const WRAP_LINES_CLASSES =
  '[&_code]:whitespace-normal [&_.line]:block [&_.line]:whitespace-pre-wrap [&_.line]:break-words';

// Indents each wrapped line by its own leading whitespace: padding-left pushes
// the whole line right, and an equal negative text-indent pulls the first
// visual line (which already renders those leading spaces) back to column 0.
const hangingIndentTransformer = (code: string): ShikiTransformer => {
  const lines = code.split('\n');
  return {
    name: 'hanging-indent',
    line(node: Element, line: number) {
      const text = lines[line - 1] ?? '';
      const leading = text.length - text.trimStart().length;
      if (leading === 0) {
        return;
      }
      node.properties ??= {};
      const prev =
        typeof node.properties['style'] === 'string'
          ? `${node.properties['style']};`
          : '';
      node.properties[
        'style'
      ] = `${prev}padding-left:${leading}ch;text-indent:-${leading}ch`;
    },
  };
};

type CodeBlockContextType = {
  code: string;
};

const CodeBlockContext = createContext<CodeBlockContextType>({
  code: '',
});

const lineNumberTransformer: ShikiTransformer = {
  name: 'line-numbers',
  line(node: Element, line: number) {
    node.children.unshift({
      type: 'element',
      tagName: 'span',
      properties: {
        className: [
          'inline-block',
          'min-w-10',
          'mr-4',
          'text-right',
          'select-none',
          'text-muted-foreground',
        ],
      },
      children: [{ type: 'text', value: String(line) }],
    });
  },
};

export async function highlightCode(
  code: string,
  language: BundledLanguage,
  showLineNumbers = false,
  wrapLines = false
) {
  const transformers: ShikiTransformer[] = [];
  if (showLineNumbers) {
    transformers.push(lineNumberTransformer);
  }
  if (wrapLines) {
    transformers.push(hangingIndentTransformer(code));
  }

  try {
    return await Promise.all([
      codeToHtml(code, {
        lang: language,
        theme: 'one-light',
        transformers,
      }),
      codeToHtml(code, {
        lang: language,
        theme: 'one-dark-pro',
        transformers,
      }),
    ]);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to highlight code', error);
    return ['', ''] as [string, string];
  }
}

export const CodeBlock = ({
  code,
  language,
  showLineNumbers = false,
  wrapLines = false,
  className,
  children,
  ...props
}: CodeBlockProps) => {
  const [html, setHtml] = useState<string>('');
  const [darkHtml, setDarkHtml] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const [light, dark] = await highlightCode(
          code,
          language,
          showLineNumbers,
          wrapLines
        );
        if (cancelled) {
          return;
        }
        setHtml(light);
        setDarkHtml(dark);
      } catch (error) {
        if (cancelled) {
          return;
        }
        // eslint-disable-next-line no-console
        console.error('Failed to highlight code', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [code, language, showLineNumbers, wrapLines]);

  return (
    <CodeBlockContext.Provider value={{ code }}>
      <div
        className={cn(
          'group relative w-full max-h-96 overflow-auto rounded-md border bg-card/60 text-foreground',
          wrapLines && WRAP_LINES_CLASSES,
          className
        )}
        {...props}
      >
        <div className="relative">
          <div
            className="dark:hidden [&>pre]:m-0 [&>pre]:bg-card! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div
            className="hidden dark:block [&>pre]:m-0 [&>pre]:bg-card! [&>pre]:p-4 [&>pre]:text-foreground! [&>pre]:text-sm [&_code]:font-mono [&_code]:text-sm"
            // biome-ignore lint/security/noDangerouslySetInnerHtml: "this is needed."
            dangerouslySetInnerHTML={{ __html: darkHtml }}
          />
          {children !== undefined && (
            <div className="absolute top-2 right-2 flex items-center gap-2">
              {children}
            </div>
          )}
        </div>
      </div>
    </CodeBlockContext.Provider>
  );
};

export type CodeBlockCopyButtonProps = ComponentProps<typeof Button> & {
  onCopy?: () => void;
  onError?: (error: Error) => void;
  timeout?: number;
};

export const CodeBlockCopyButton = ({
  onCopy,
  onError,
  timeout = 2000,
  children,
  className,
  ...props
}: CodeBlockCopyButtonProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const { code } = useContext(CodeBlockContext);

  const copyToClipboard = async () => {
    if (
      typeof window === 'undefined' ||
      navigator?.clipboard?.writeText === undefined
    ) {
      onError?.(new Error('Clipboard API not available'));
      return;
    }

    try {
      await navigator.clipboard.writeText(code);
      setIsCopied(true);
      onCopy?.();
      setTimeout(() => setIsCopied(false), timeout);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const Icon = isCopied ? CheckIcon : CopyIcon;

  return (
    <Button
      className={cn('shrink-0', className)}
      onClick={copyToClipboard}
      size="icon"
      variant="ghost"
      {...props}
    >
      {children ?? <Icon size={14} />}
    </Button>
  );
};
